<template>
  <div class="card-manipulated-recipe">
    <div>
      <div
        class="signed-card-signature"
        :class="{ 'is-signed': isSigned }"
        v-if="signature"
      >
        <signature-banner
          :signature="signature"
          :discarding="discardingSignature"
          @retry="retrySignature"
          @discard="discardSignature"
        >
          <button
            v-if="isSigned"
            slot="actions"
            type="button"
            class="btn btn-sm btn-gray-outline btn-icon btn-icon-left"
            :class="{ loading: sendingWhatsapp, tooltip: !isWhatsappSendEnabled }"
            data-tooltip="Envio pelo WhatsApp não habilitado"
            :disabled="sendingWhatsapp"
            @click="sendWhatsapp"
          ><fa-icon :icon="['fab', 'whatsapp']" />Enviar pelo WhatsApp</button>
          <st-button-report
            v-if="isSigned"
            slot="actions"
            type="pdf"
            button-class="btn-sm btn-primary"
            label="Imprimir receita assinada"
            :icon="['fal', 'file-signature']"
            :options="null"
            :route="printLink()"
          ></st-button-report>
        </signature-banner>
      </div>
    </div>
    <div>
    <div class="signed-card-content" v-if="isSigned">
      <signed-sheet
        icon="flask"
        title="Receita manipulada"
        subtitle="Fórmula assinada digitalmente, disponível apenas para leitura"
        :date="printDate && data.date ? data.date : ''"
      >
        <div class="signed-sheet-html" v-if="data.notes" v-html="data.notes"></div>
        <div class="signed-sheet-empty" v-else>Receita sem conteúdo</div>
      </signed-sheet>
    </div>
    <fieldset
      v-else
      class="card-recipe-fieldset"
      :disabled="isSignatureLocked"
    >
    <div class="card-recipe-header">
      <div class="columns">
        <div class="column col-2 col-md-3 col-sm-12"
             :class="{'has-error': $v.data.date.$error}">
          <dx-input-date class="text-center"
                         v-model="data.date"
                         @blur="$v.data.date.$touch()"
                         :disabled="!printDate"
                         @change="getDate"
          />
        </div>
        <div class="column col-auto hide-sm">
          <label class="form-checkbox">
            <input type="checkbox" v-model="printDate">
            <i class="form-icon"></i>Imprimir data
          </label>
        </div>
        <div class="column hide-sm">
          <drug-check-status :loading="checkingDrugs" />
        </div>
        <div class="column col-auto col-md-auto col-sm-12">
          <div class="input-group" :class="{loading: loadingModel}">
            <select class="form-select" v-model="modelId"
                    :disabled="loadingModel"
                    @change="getModel">
              <option value="">Meus modelos de receituários</option>
              <option v-for="(item, i) in models" :value="item.id" :key="i">
                {{ item.name }}
              </option>
            </select>
            <button class="btn btn-neutral input-group-btn"
                    @click="openEditModel">
              Gerenciar
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="card-recipe-body">
      <drug-search @select="setDrug" />
      <div class="divider" />
      <drug-alerts
        :medical-record-id="medicalRecordId"
        :text="data.notes"
        @loading="checkingDrugs = $event"
      />
      <st-editor
        id="manipulated-recipe-content"
        :disabled="isSignatureLocked"
        v-model="data.notes"
      ></st-editor>
    </div>
    </fieldset>
    </div>
    <div>
    <div class="card-recipe-footer" v-if="!isSigned">
      <div class="d-flex centered">
        <button
          type="button"
          class="btn btn-primary btn-icon btn-icon-left"
          :disabled="!data.id || !data.notes || pending || isSignatureRunning"
          @click="openPrintModal"
        >
          <fa-icon :icon="['fal', 'print']"/>
          <span v-if="pending">Salvando...</span>
          <span v-else>Gerar receita</span>
        </button>
        <button
          class="btn btn-icon-left btn-error ml-2"
          :disabled="pending || isSignatureLocked"
          @click="remove"
        ><fa-icon :icon="['fal', 'trash']"/>Excluir</button>
      </div>
    </div>
    </div>
    <modal-edit
      v-if="editModels.show"
      :show="editModels.show"
      @close="closeEditModel"
    />
  </div>
</template>

<script>
import { date } from 'src/data/validators';
import * as unit from 'src/data/drug-units';
import * as access from 'src/data/drug-accesses';
import { mapState } from 'vuex';
import moment from 'moment';
import { drugName } from '@/helpers/medicine';
import ModalEdit from '../../../../template/components/recipe/modals/Main.vue';
import DrugAlerts from '../../assistant/DrugAlerts.vue';
import DrugCheckStatus from '../../assistant/DrugCheckStatus.vue';
import DrugSearch from './DrugSearch.vue';
import formMixin from '../../../../../mixins/form';
import digitalSignatureMixin from '../../../../../mixins/digital-signature';
import SignatureBanner from '../../../../../components/digital-signature/SignatureBanner.vue';
import SignedSheet from '../../../../../components/digital-signature/SignedSheet.vue';
import { MEDICAL_RECORD_RECIPE } from '../../../../../data/digital-signature';
import { whatsappText } from '../../../../../helpers/digital-prescription';

const BLANK_LINE = '<p>&nbsp;</p>';

export default {
  mixins: [formMixin, digitalSignatureMixin],
  components: {
    ModalEdit,
    DrugAlerts,
    DrugCheckStatus,
    DrugSearch,
    SignatureBanner,
    SignedSheet,
  },
  props: {
    medicalRecordId: {
      type: String,
    },
    patient: {
      type: Object,
      default: null,
    },
    dosages: {
      type: Array,
    },
    data: {
      key: {
        type: String,
      },
      id: {
        type: String,
      },
      date: {
        type: Date,
      },
      notes: {
        type: String,
      },
      drugs: {
        type: Array,
      },
    },
  },
  data() {
    return {
      models: [],
      modelId: '',
      printDate: true,
      loadingModel: false,
      checkingDrugs: false,
      pending: false,
      printing: false,
      savingModel: false,
      debounceTimeout: null,
      editModels: {
        show: false,
      },
    };
  },
  created() {
    this.signature = this.data.digitalSignature || null;
  },
  computed: {
    haveDrugs() {
      return this.data.drugs.length > 0;
    },
    ...mapState({
      user: state => state.auth.user,
    }),
    printParams() {
      return {
        simpleRecipe: true,
        simpleDate: this.printDate,
        simpleQuantity: 1,
      };
    },
  },
  validations() {
    return {
      data: {
        date: { date },
      },
    };
  },
  watch: {
    data: {
      handler() {
        if (this.isSignatureLocked) {
          return;
        }
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = setTimeout(() => {
          this.debounceTimeout = null;
          if (this.isSignatureLocked) {
            return;
          }
          this.pending = true;
          this.save();
        }, 1000);
      },
      deep: true,
    },
    printDate: function printDate() {
      if (this.isSignatureLocked) {
        return;
      }
      this.data.date = this.printDate ? moment().format('YYYY-MM-DD') : '';
    },
  },
  mounted() {
    this.loadModels();
    this.printDate = !!this.data.date;
  },
  methods: {
    getDate() {
      if (!this.data.date || !moment(this.data.date).isValid()) {
        this.data.date = moment().format('YYYY-MM-DD');
      }
    },
    loadModels() {
      const params = {
        active: true,
        limit: 0,
        professionalId: this.user.id,
      };
      this.$httpX.get('/recipe-templates', { params })
        .then(({ data }) => {
          this.models = data.items;
        })
        .catch(this.$toast.error);
    },
    getModel() {
      this.loadingModel = true;
      this.$httpX.get(`/recipe-templates/${this.modelId}`)
        .then(({ data }) => {
          let html = data.drugs.map(drug => this.drugToHtml(drug)).join('');
          if (data.notes) {
            html += `${this.toParagraphs(this.splitLines(data.notes))}${BLANK_LINE}`;
          }
          this.appendNotes(html);
        })
        .catch(this.$toast.error)
        .then(() => {
          this.modelId = '';
          this.loadingModel = false;
        });
    },
    setDrug(data) {
      this.appendNotes(this.drugToHtml({
        name: drugName(data),
        quantity: 1,
        unit: data.unit || 'packing',
        access: data.access || 'oral',
      }));
    },
    drugToHtml(drug) {
      let title = `<strong>${this.escapeHtml((drug.name || '').trim())}</strong>`;

      if (drug.quantity) {
        const quantity = [
          `${drug.quantity} ${unit.getName(drug.unit) || ''}`.trim(),
          access.getName(drug.access),
        ].filter(part => part).join(' - ');
        title += ` - ${this.escapeHtml(quantity)}`;
      }

      const dosage = drug.dosage ? this.splitLines(drug.dosage) : [];

      return `<p>${title}</p>${this.toParagraphs(dosage)}${BLANK_LINE}`;
    },
    splitLines(text) {
      return text.split('\n')
        .map(line => line.trim())
        .filter(line => line);
    },
    toParagraphs(lines) {
      return lines
        .map(line => `<p>${this.escapeHtml(line)}</p>`)
        .join('');
    },
    appendNotes(html) {
      if (!html) {
        return;
      }

      this.data.notes = `${this.data.notes || ''}${html}`;
    },
    escapeHtml(text) {
      const element = document.createElement('div');
      element.textContent = text;
      return element.innerHTML;
    },
    openEditModel() {
      this.editModels.show = true;
    },
    closeEditModel() {
      this.loadModels();
      this.editModels.show = false;
    },
    save() {
      const data = this.clone(this.data);
      data.printDate = this.printDate;

      if (data.date && data.date.length < 10) {
        data.date = '';
      }

      const request = !this.data.id
        ? this.$httpX
          .post(`/medical-records/${this.medicalRecordId}/recipes`, data)
        : this.$httpX
          .put(`/medical-records/${this.medicalRecordId}/recipes/${this.data.id}`, data);

      return request
        .then(({ data: result }) => {
          this.data.id = result.id;
          return true;
        })
        .catch(() => {
          this.$toast.error('Ocorreu um erro ao salvar este registro. Tente novamente!');
          return false;
        })
        .finally(() => {
          this.pending = false;
        });
    },
    remove() {
      this.$dialog.show('', {
        html:
          '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + '<div>Deseja realmente excluir este receituário?</div>'
          + '</div>',
        buttons: [
          {
            label: 'Não',
            classes: '',
          }, {
            label: 'Sim',
            classes: 'btn-primary btn-error ml-2',
            click: (close) => {
              this.$emit('remove');
              close();
            },
          },
        ],
      });
    },
    saveModel() {
      const drugs = this.data.drugs
        .filter(item => (
          item.name && item.quantity
        ));

      if (!drugs) {
        return;
      }

      this.savingModel = true;

      const names = drugs
        .map(drug => drug.name.split(' ')[0]);

      const data = {
        id: this.modelId,
        name: names.join(' + '),
        professionals: [
          { id: this.user.id, name: this.user.name },
        ],
        active: true,
        drugs,
      };

      const request = !data.id
        ? this.$httpX.post('/recipe-templates', data)
        : this.$httpX.put(`/recipe-templates/${data.id}`, data);

      // eslint-disable-next-line consistent-return
      return request
        .then(({ data: result }) => {
          const found = this.models.find(model => model.id === result.id);
          if (!found) {
            this.models.push({
              id: result.id,
              name: result.name,
              drugs: result.drugs.length,
            });
          } else {
            found.drugs = result.drugs.length;
          }
          this.modelId = result.id;
          this.$toast.show('Salvo com sucesso!');
        })
        .catch(this.$toast.error)
        .then(() => {
          this.savingModel = false;
        });
    },
    printLink() {
      return `/medical-records/${this.medicalRecordId}/recipes/${this.data.id}/print`;
    },
    isOwnDigitalSignature(data) {
      return data.type === MEDICAL_RECORD_RECIPE
        && !!this.data.id
        && data.itemId === this.data.id;
    },
    openPrintModal(startSigning = false) {
      if (this.pending || !this.data.id) {
        return;
      }
      this.$printRecipe.open(this, {
        medicalRecordId: this.medicalRecordId,
        recipe: this.data,
        patient: this.patient,
        professionalName: this.user ? this.user.name : '',
        printDate: this.printDate,
        startSigning: startSigning === true,
      }, {
        sign: this.signRecipe,
      });
    },
    async signRecipe({ showDate, pageSize, whatsappCellphone }) {
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = null;
        if (!await this.save()) {
          return;
        }
      }

      if (!this.data.id) {
        return;
      }

      this.requestDigitalSignature(this.medicalRecordId, {
        type: MEDICAL_RECORD_RECIPE,
        itemId: this.data.id,
        showDate,
        pageSize,
        whatsappCellphone: whatsappCellphone || '',
      }, () => {
        this.$printRecipe.close();
      });
    },
    retrySignature() {
      this.openPrintModal(true);
    },
    discardSignature() {
      this.discardDigitalSignature('receita');
    },
    sendWhatsapp() {
      this.sendDigitalSignatureWhatsapp(this.patient ? this.patient.cellphone : '');
    },
    digitalSignatureWhatsappText(documentUrl) {
      return whatsappText({
        patientName: this.patient ? this.patient.name : '',
        professionalName: this.user ? this.user.name : '',
        documentUrl,
      });
    },
  },
};
</script>

<style lang="scss">
@import 'src/assets/scss/variables';
.card-manipulated-recipe {
  .divider {
    border-top: $border-width * 2 solid $light-color;
    margin: 1rem 0;
  }
  background-color: lighten($gray-color, 24%);
  border: $border-width solid $border-color;
  border-radius: $border-radius;
  margin-bottom: $layout-spacing-lg;
  .card-recipe-fieldset {
    border: 0;
    margin: 0;
    min-width: 0;
    padding: 0;
  }
  .card-recipe-header {
    background-color: lighten($gray-color, 15%);
    padding: $layout-spacing;
  }
  .card-recipe-body {
    padding: $layout-spacing-lg $layout-spacing;
  }
  .card-recipe-footer {
    padding: $layout-spacing-lg;
    text-align: center;
    button {
      min-width: 7rem;
    }
  }

  .document-editor-body {
    background-color: $light-color;
    .ck.ck-content.ck-editor__editable {
      min-height: 15rem;
    }
  }
}
</style>
